import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AdminDiscount,
  type DiscountInput,
  type DiscountKind,
  type DiscountsResponse,
} from "../lib/api.ts";
import { formatGbp, formatShortDate, todayKey } from "../lib/format.ts";

interface OffersEditorProps {
  token: string;
  onUnauthorised: () => void;
}

/**
 * Timed offers: a percentage or an amount off, either one treatment or all of
 * them, between two dates.
 *
 * The form deliberately takes dates rather than instants. An offer "from the 1st
 * of December to the 31st of January" is what the studio means, and the API turns
 * that into the right instants - including making the last day whole.
 */

type Draft = {
  label: string;
  scope: "all" | string;
  kind: DiscountKind;
  /** Percent, or pounds for an amount off. Held as text so a half-typed number survives. */
  value: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
};

const EMPTY: Draft = {
  label: "",
  scope: "all",
  kind: "percent",
  value: "",
  startDate: todayKey(),
  endDate: "",
  isActive: true,
};

function toDraft(discount: AdminDiscount): Draft {
  return {
    label: discount.label,
    scope: discount.serviceId === null ? "all" : String(discount.serviceId),
    kind: discount.kind,
    // Amounts are stored in pence and entered in pounds.
    value:
      discount.kind === "percent"
        ? String(discount.value)
        : (discount.value / 100).toFixed(2),
    startDate: discount.startDate,
    endDate: discount.endDate ?? "",
    isActive: discount.isActive,
  };
}

function toInput(draft: Draft): DiscountInput {
  return {
    label: draft.label.trim(),
    serviceId: draft.scope === "all" ? null : Number(draft.scope),
    kind: draft.kind,
    value: Number(draft.value),
    startDate: draft.startDate,
    endDate: draft.endDate || undefined,
    isActive: draft.isActive,
  };
}

/** What the offer is doing right now, in one word plus a tone. */
function state(discount: AdminDiscount): { label: string; tone: string } {
  if (!discount.isActive) return { label: "Paused", tone: "dead" };
  if (discount.finished) return { label: "Finished", tone: "dead" };
  if (discount.scheduled) return { label: "Scheduled", tone: "pending" };
  if (discount.live) return { label: "Running now", tone: "ok" };
  return { label: "Not running", tone: "dead" };
}

function describeWindow(discount: AdminDiscount): string {
  const from = formatShortDate(`${discount.startDate}T12:00:00Z`);
  if (!discount.endDate) return `From ${from}, until switched off`;
  if (discount.endDate === discount.startDate) return `On ${from}`;
  return `${from} to ${formatShortDate(`${discount.endDate}T12:00:00Z`)}`;
}

export function OffersEditor({ token, onUnauthorised }: OffersEditorProps) {
  const [data, setData] = useState<DiscountsResponse | null>(null);
  const [treatments, setTreatments] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.admin.getDiscounts(token);
      setData(result);
      setTreatments(result.treatments.map((t) => ({ id: t.id, name: t.name })));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Offers could not be loaded.",
      );
    }
  }, [token, onUnauthorised]);

  useEffect(() => {
    void load();
  }, [load]);

  function startNew() {
    setDraft(EMPTY);
    setEditingId(null);
    setShowForm(true);
    setMessage(null);
    setError(null);
  }

  function startEdit(discount: AdminDiscount) {
    setDraft(toDraft(discount));
    setEditingId(discount.id);
    setShowForm(true);
    setMessage(null);
    setError(null);
  }

  const numeric = Number(draft.value);
  const problems: string[] = [];
  if (draft.label.trim().length < 2)
    problems.push("Give the offer a name clients will understand.");
  if (!Number.isFinite(numeric) || numeric <= 0)
    problems.push("Say how much comes off.");
  if (draft.kind === "percent" && data && numeric > data.maxPercent) {
    problems.push(
      `A percentage offer cannot be more than ${data.maxPercent}%.`,
    );
  }
  if (!draft.startDate) problems.push("Give a start date.");
  if (draft.endDate && draft.endDate < draft.startDate) {
    problems.push("The offer cannot end before it starts.");
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const input = toInput(draft);
      const saved = editingId
        ? await api.admin.updateDiscount(token, editingId, input)
        : await api.admin.createDiscount(token, input);
      setMessage(
        saved.live
          ? `"${saved.label}" is running now - prices on the website have changed.`
          : `"${saved.label}" saved. It is not running yet.`,
      );
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That offer could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function togglePaused(discount: AdminDiscount) {
    setBusy(true);
    setError(null);
    try {
      await api.admin.updateDiscount(token, discount.id, {
        ...toInput(toDraft(discount)),
        isActive: !discount.isActive,
      });
      setMessage(
        discount.isActive
          ? `"${discount.label}" is paused. Prices are back to normal.`
          : `"${discount.label}" is switched back on.`,
      );
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That could not be changed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(discount: AdminDiscount) {
    if (
      !window.confirm(
        `Delete "${discount.label}"?\n\n` +
          "Appointments already booked under it keep the price they were given - " +
          "a booking records its own price and the offer it came from.",
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.admin.deleteDiscount(token, discount.id);
      setMessage(`"${discount.label}" deleted.`);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That could not be deleted.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return error ? (
      <p className="notice notice--error">{error}</p>
    ) : (
      <p className="booking__loading">
        <span className="spinner" /> Loading offers…
      </p>
    );
  }

  const discounted = data.treatments.filter((t) => t.savingPence > 0);

  return (
    <div className="stack-lg">
      <div>
        <h2 className="admin__section-title">Offers</h2>
        <p className="field-hint">
          A percentage or an amount off, on one treatment or all of them,
          between two dates. Leave the end date empty and it runs until you
          switch it off.
        </p>
      </div>

      <p className="notice">
        Where two offers could apply to the same treatment, the client gets
        whichever saves them more - <strong>offers never add together</strong>.
        Deposits follow the offer price, so a 20% offer means a 20% smaller
        deposit too.
      </p>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      {discounted.length > 0 && (
        <section className="offer-preview">
          <h3 className="content-section__title">Prices right now</h3>
          <ul className="offer-preview__list">
            {discounted.map((row) => (
              <li key={row.id}>
                <span className="offer-preview__name">{row.name}</span>
                <span className="offer-preview__prices">
                  <s>{formatGbp(row.originalPence)}</s>{" "}
                  <strong>{formatGbp(row.pricePence)}</strong>
                </span>
                <span className="offer-preview__label">{row.offerLabel}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!showForm && (
        <div className="row">
          <button type="button" className="btn" onClick={startNew}>
            Add an offer
          </button>
        </div>
      )}

      {showForm && (
        <form
          className="panel admin-form stack-lg"
          onSubmit={(event) => {
            event.preventDefault();
            if (problems.length === 0) void save();
          }}
        >
          <h3 className="admin-form__title">
            {editingId ? "Edit offer" : "New offer"}
          </h3>

          <div className="admin-form__grid">
            <div className="field">
              <label htmlFor="offer-label">Name</label>
              <input
                id="offer-label"
                className="input"
                maxLength={80}
                placeholder="Winter special"
                value={draft.label}
                onChange={(event) =>
                  setDraft({ ...draft, label: event.target.value })
                }
              />
              <p className="field-hint">Clients see this next to the price.</p>
            </div>

            <div className="field">
              <label htmlFor="offer-scope">Applies to</label>
              <select
                id="offer-scope"
                className="select"
                value={draft.scope}
                onChange={(event) =>
                  setDraft({ ...draft, scope: event.target.value })
                }
              >
                <option value="all">All treatments</option>
                {treatments.map((treatment) => (
                  <option key={treatment.id} value={String(treatment.id)}>
                    {treatment.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="offer-kind">Type</label>
              <select
                id="offer-kind"
                className="select"
                value={draft.kind}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    kind: event.target.value as DiscountKind,
                    value: "",
                  })
                }
              >
                <option value="percent">Percentage off</option>
                <option value="amount">Amount off</option>
              </select>
            </div>

            <div className="field">
              <label htmlFor="offer-value">
                {draft.kind === "percent" ? "Percent off" : "Pounds off"}
              </label>
              <div className="row">
                {draft.kind === "amount" && (
                  <span className="settings-field__unit">£</span>
                )}
                <input
                  id="offer-value"
                  className="input input--compact"
                  type="number"
                  min={draft.kind === "percent" ? 1 : 0.01}
                  max={draft.kind === "percent" ? data.maxPercent : undefined}
                  step={draft.kind === "percent" ? 1 : 0.01}
                  value={draft.value}
                  onChange={(event) =>
                    setDraft({ ...draft, value: event.target.value })
                  }
                />
                {draft.kind === "percent" && (
                  <span className="settings-field__unit">%</span>
                )}
              </div>
            </div>

            <div className="field">
              <label htmlFor="offer-start">First day</label>
              <input
                id="offer-start"
                className="input"
                type="date"
                value={draft.startDate}
                onChange={(event) =>
                  setDraft({ ...draft, startDate: event.target.value })
                }
              />
            </div>

            <div className="field">
              <label htmlFor="offer-end">Last day</label>
              <input
                id="offer-end"
                className="input"
                type="date"
                min={draft.startDate}
                value={draft.endDate}
                onChange={(event) =>
                  setDraft({ ...draft, endDate: event.target.value })
                }
              />
              <p className="field-hint">
                Leave empty to run until you switch it off. The last day is
                included in full.
              </p>
            </div>
          </div>

          <label className="builder-item__required" htmlFor="offer-active">
            <input
              id="offer-active"
              type="checkbox"
              checked={draft.isActive}
              onChange={(event) =>
                setDraft({ ...draft, isActive: event.target.checked })
              }
            />
            <span>
              Switched on. Uncheck to prepare an offer without it going live.
            </span>
          </label>

          {problems.length > 0 && (
            <ul className="admin-form__errors">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}

          <div className="row">
            <button
              type="submit"
              className="btn"
              disabled={busy || problems.length > 0}
            >
              {busy ? (
                <span className="spinner" />
              ) : editingId ? (
                "Save offer"
              ) : (
                "Create offer"
              )}
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {data.discounts.length === 0 ? (
        <p className="notice">
          No offers yet. Prices on the website are the menu prices.
        </p>
      ) : (
        <ul className="offer-list">
          {data.discounts.map((discount) => {
            const badge = state(discount);
            return (
              <li
                key={discount.id}
                className={`offer-item offer-item--${badge.tone}`}
              >
                <div className="offer-item__main">
                  <h3 className="offer-item__label">{discount.label}</h3>
                  <p className="offer-item__what">
                    <strong>{discount.descriptor}</strong>
                    {" · "}
                    {discount.siteWide
                      ? "all treatments"
                      : discount.serviceName}
                  </p>
                  <p className="offer-item__when">{describeWindow(discount)}</p>
                </div>

                <div className="offer-item__state">
                  <span className={`pill pill--${badge.tone}`}>
                    {badge.label}
                  </span>
                </div>

                <div className="offer-item__actions">
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => startEdit(discount)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => togglePaused(discount)}
                  >
                    {discount.isActive ? "Pause" : "Switch on"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet offer-item__delete"
                    disabled={busy}
                    onClick={() => remove(discount)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
